/**
 * Filter upgrades for the collection/search facets (snippets/facets.liquid).
 *
 * facets.js replaces the facet markup wholesale after every filter change, so all
 * state is read from the DOM in connectedCallback rather than bound once on load.
 */

const TD_FACETS_SELECTORS = {
  searchInput: '[data-td-facet-search-input]',
  searchItem: '[data-td-facet-search-item]',
  searchEmpty: '[data-td-facet-search-empty]',
  searchStatus: '[data-td-facet-search-status]',
  clearButton: '[data-td-facets-clear-button]',
};

// facets.js replaces the whole filter sidebar after each filter change, which recreates these
// elements. Keep each search's query and list scroll position (keyed by input id) so ticking a
// brand mid-search doesn't wipe the search. Cleared on form reset.
const tdFacetSearchState = new Map();

// Lower-case and strip accents so "chloe" matches "Chloé".
const tdFacetNormalize = (text) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks
    .toLowerCase()
    .trim();

/**
 * Filters a long facet list (Inspired by Brand) by a typed query.
 * The input lives inside the filter form, so its input events are stopped here —
 * otherwise FacetFiltersForm would re-fetch the product grid on every keystroke.
 */
class TdFacetSearch extends HTMLElement {
  constructor() {
    super();
    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.resetTimer = null;
    this.scrollFrame = null;
  }

  connectedCallback() {
    this.input = this.querySelector(TD_FACETS_SELECTORS.searchInput);
    this.form = this.closest('form');
    this.empty = this.querySelector(TD_FACETS_SELECTORS.searchEmpty);
    this.status = this.querySelector(TD_FACETS_SELECTORS.searchStatus);
    this.items = Array.from(this.querySelectorAll(TD_FACETS_SELECTORS.searchItem)).map((element) => ({
      element,
      text: tdFacetNormalize(element.dataset.tdFacetSearchItem || ''),
    }));
    this.total = this.items.length;
    this.defaultStatus = this.status?.textContent || '';
    this.resultsTemplate = this.status?.dataset.resultsTemplate || '[shown] / [total]';
    if (!this.input) return;

    this.list = this.querySelector('ul');
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.form?.addEventListener('reset', this.onReset);
    this.list?.addEventListener('scroll', this.onScroll, { passive: true });
    this.restoreState();
  }

  restoreState() {
    const state = tdFacetSearchState.get(this.input.id);
    if (!state) return;
    if (state.query) {
      this.input.value = state.query;
      this.filter(state.query);
    }
    // The sidebar is laid out after this element reconnects, so restore scroll on the next frame.
    if (this.list && state.scrollTop) {
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.list.scrollTop = state.scrollTop;
      });
    }
  }

  saveState() {
    tdFacetSearchState.set(this.input.id, {
      query: this.input.value,
      scrollTop: this.list?.scrollTop || 0,
    });
  }

  onScroll() {
    this.saveState();
  }

  disconnectedCallback() {
    clearTimeout(this.resetTimer);
    this.resetTimer = null;
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = null;
    this.form?.removeEventListener('reset', this.onReset);
    this.list?.removeEventListener('scroll', this.onScroll);
    if (!this.input) return;
    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('keydown', this.onKeydown);
  }

  // form.reset() empties the input without an input event, so re-show the full list once it has run.
  onReset() {
    clearTimeout(this.resetTimer);
    tdFacetSearchState.delete(this.input.id);
    this.resetTimer = setTimeout(() => {
      this.resetTimer = null;
      this.filter('');
    }, 0);
  }

  onInput(event) {
    event.stopPropagation();
    this.filter(this.input.value);
    this.saveState();
  }

  onKeydown(event) {
    // Enter would submit the filter form; Escape clears the query first.
    if (event.key === 'Enter') event.preventDefault();
    if (event.key === 'Escape' && this.input.value) {
      event.stopPropagation();
      this.input.value = '';
      this.filter('');
      this.saveState();
    }
  }

  filter(rawQuery) {
    const query = tdFacetNormalize(rawQuery);
    let shown = 0;

    this.items.forEach(({ element, text }) => {
      const match = !query || text.includes(query);
      element.hidden = !match;
      if (match) shown += 1;
    });

    if (this.empty) this.empty.hidden = shown > 0;
    if (this.status) {
      this.status.textContent = query
        ? this.resultsTemplate.replace('[shown]', shown).replace('[total]', this.total)
        : this.defaultStatus;
    }
  }
}

if (!customElements.get('td-facet-search')) {
  customElements.define('td-facet-search', TdFacetSearch);
}

/**
 * Clear button in the mobile Filter & sort drawer (replaces Dawn's Clear all link and the old Exit).
 * - Filters applied: untick everything and submit, which shows all products and closes the drawer.
 * - Only unapplied ticks: reset the form so nothing is selected; the drawer stays open.
 * - Nothing to clear: close the drawer.
 * Closing the drawer any other way (the ×) also discards unapplied ticks.
 * defaultChecked / defaultValue are the server-rendered, i.e. applied, state.
 */
class TdFacetsClear extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.submitting = false;
  }

  connectedCallback() {
    this.form = this.closest('form');
    this.details = this.closest('details');
    this.button = this.querySelector(TD_FACETS_SELECTORS.clearButton);

    this.button?.addEventListener('click', this.onClick);
    this.form?.addEventListener('submit', this.onSubmit);
    this.details?.addEventListener('toggle', this.onToggle);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.details?.removeEventListener('toggle', this.onToggle);
  }

  get filterInputs() {
    return Array.from(this.form?.elements || []).filter((element) => element.name?.startsWith('filter.'));
  }

  static isToggle(element) {
    return element.type === 'checkbox' || element.type === 'radio';
  }

  hasApplied() {
    return this.filterInputs.some((element) =>
      TdFacetsClear.isToggle(element) ? element.defaultChecked : element.defaultValue !== ''
    );
  }

  hasPending() {
    return this.filterInputs.some((element) =>
      TdFacetsClear.isToggle(element) ? element.checked !== element.defaultChecked : element.value !== element.defaultValue
    );
  }

  onClick(event) {
    event.preventDefault();

    if (this.hasApplied()) {
      this.filterInputs.forEach((element) => {
        if (TdFacetsClear.isToggle(element)) {
          element.checked = false;
        } else {
          element.value = '';
        }
      });
      this.form.requestSubmit();
      return;
    }

    if (this.hasPending()) {
      this.form.reset();
      return;
    }

    this.closeDrawer(event);
  }

  onSubmit() {
    this.submitting = true;
  }

  onToggle() {
    if (this.details.open) return;
    if (!this.submitting) this.form?.reset();
    this.submitting = false;
  }

  closeDrawer(event) {
    const drawer = this.closest('menu-drawer');
    // Dawn's menu-drawer markup is not TD-editable; its summary has no data hook.
    const summary = drawer?.querySelector('details > summary');
    drawer?.closeMenuDrawer(event, summary);
  }
}

if (!customElements.get('td-facets-clear')) {
  customElements.define('td-facets-clear', TdFacetsClear);
}

/**
 * Live result count in the mobile drawer. The mobile form only applies on submit, so as boxes are
 * ticked this fetches the section render for the pending selection and updates the header count
 * and the Apply label ("Apply (24)") without touching the grid. A form reset restores them.
 */
class TdFacetsCountPreview extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.onReset = this.onReset.bind(this);
    this.timer = null;
    this.controller = null;
  }

  connectedCallback() {
    this.form = this.closest('form');
    if (!this.form) return;

    this.itemsEl = this.form.querySelector('[data-td-facets-count="items"]');
    this.applyEl = this.form.querySelector('[data-td-facets-count="apply"]');
    // Server-rendered = applied count. facets.js re-renders the whole drawer after an apply,
    // so this element is recreated with the new applied count each time.
    this.appliedCount = Number(this.itemsEl?.dataset.count || 0);

    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('reset', this.onReset);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('reset', this.onReset);
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  onChange(event) {
    // The brand search input has no name and never changes the result set.
    if (!event.target.name) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), 250);
  }

  onReset() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.render(this.appliedCount);
  }

  async refresh() {
    // The section id sits on the filters <aside data-id> that wraps this drawer (Dawn markup).
    const sectionId = this.closest('[data-id]')?.dataset.id;
    if (!sectionId) return;

    this.controller?.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams(new FormData(this.form));
    params.set('section_id', sectionId);
    this.applyEl?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.location.pathname}?${params.toString()}`, {
        signal: this.controller.signal,
      });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const count = html.querySelector('[data-td-facets-count="items"]')?.dataset.count;
      if (count !== undefined) this.render(Number(count));
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.applyEl?.removeAttribute('aria-busy');
    }
  }

  render(count) {
    if (this.itemsEl) {
      const template = count === 1 ? this.dataset.itemsTemplateOne : this.dataset.itemsTemplate;
      this.itemsEl.textContent = (template || '[count]').replace('[count]', count);
    }
    if (this.applyEl?.dataset.labelTemplate) {
      this.applyEl.textContent = this.applyEl.dataset.labelTemplate.replace('[count]', count);
    }
  }
}

if (!customElements.get('td-facets-count-preview')) {
  customElements.define('td-facets-count-preview', TdFacetsCountPreview);
}
